import { Children, forwardRef, useRef } from "react";
import { useReducedMotion, useScroll, useTransform } from "motion/react";
import * as m from "motion/react-m";
import { motionTokens } from "./tokens";
import { useLandingViewport } from "./useLandingMobile";

const evidenceReveal = {
  hidden: { opacity: 0, y: motionTokens.distance.editorial, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: motionTokens.duration.deliberate,
      ease: motionTokens.easing.entrance,
    },
  },
};

export const EvidenceStoryMotion = forwardRef(function EvidenceStoryMotion(
  { children, disableDepth = false, ...props },
  forwardedRef,
) {
  const localRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const landingViewport = useLandingViewport();
  const viewport = { amount: landingViewport.amount, margin: landingViewport.margin, once: true };
  const depthEnabled = !disableDepth && !shouldReduceMotion;
  const { scrollYProgress } = useScroll({
    target: localRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [-4, 4]);
  const captionsY = useTransform(scrollYProgress, [0, 1], [4, -4]);
  const setRef = (node) => {
    localRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  return (
    <m.figure
      ref={setRef}
      initial={shouldReduceMotion ? false : "hidden"}
      whileInView={shouldReduceMotion ? undefined : "visible"}
      viewport={viewport}
      variants={evidenceReveal}
      data-depth-enabled={depthEnabled ? "true" : "false"}
      {...props}
    >
      {children({
        imageStyle: { y: depthEnabled ? imageY : 0 },
        captionsStyle: { y: depthEnabled ? captionsY : 0 },
      })}
    </m.figure>
  );
});

export const EvidenceCaption = forwardRef(function EvidenceCaption(
  { children, order = 0, ...props },
  ref,
) {
  const shouldReduceMotion = useReducedMotion();
  const landingViewport = useLandingViewport();

  if (shouldReduceMotion) return <div ref={ref} {...props}>{children}</div>;

  return (
    <m.div
      ref={ref}
      initial={{ opacity: 0, y: motionTokens.distance.subtle }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: {
          delay: 0.18 + (order * 0.1),
          duration: motionTokens.duration.supporting,
          ease: motionTokens.easing.entrance,
        },
      }}
      viewport={{ amount: landingViewport.amount, margin: landingViewport.margin, once: true }}
      data-caption-order={order + 1}
      {...props}
    >
      {children}
    </m.div>
  );
});

const methodLineVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: {
      duration: 1.05,
      ease: motionTokens.easing.entrance,
    },
  },
};

const methodStepVariants = {
  hidden: { opacity: 0, y: motionTokens.distance.standard },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: [0.17, 0.53, 0.87][index],
      duration: motionTokens.duration.quick,
      ease: motionTokens.easing.entrance,
    },
  }),
};

const independentMethodStepVariants = {
  hidden: { opacity: 0, y: motionTokens.distance.standard },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: motionTokens.duration.quick,
      ease: motionTokens.easing.entrance,
    },
  },
};

export function MethodProgress({ children, independent = false, ...props }) {
  const shouldReduceMotion = useReducedMotion();
  const landingViewport = useLandingViewport();
  const viewport = { amount: landingViewport.amount, margin: landingViewport.margin, once: true };

  if (independent || shouldReduceMotion) {
    return (
      <div data-method-motion={shouldReduceMotion ? "static" : "independent"} {...props}>
        {Children.map(children, (child, index) => (
          independent && !shouldReduceMotion ? (
            <m.article
              key={child.key || index}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={independentMethodStepVariants}
            >
              {child}
            </m.article>
          ) : <article key={child.key || index}>{child}</article>
        ))}
      </div>
    );
  }

  return (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      data-method-motion="progressive"
      {...props}
    >
      <div className="method-progress-track" aria-hidden="true">
        <m.div className="method-progress-line" variants={methodLineVariants} />
      </div>
      {Children.map(children, (child, index) => (
        <m.article key={child.key || index} custom={index} variants={methodStepVariants}>
          {child}
        </m.article>
      ))}
    </m.div>
  );
}
